import { supabase } from '@/lib/supabase';

// Note: feature_definitions, feature_pricing, company_feature_overrides tables
// are not yet in the generated Database types. We cast queries as needed.
const db = supabase as any;

// =====================================================
// TYPES
// =====================================================

export interface FeatureDefinition {
  id: string;
  code: string;
  name: string;
  name_ar: string | null;
  name_fr: string | null;
  description: string | null;
  category: string;
  icon: string | null;
  is_premium: boolean;
  is_free_trial_eligible: boolean;
  free_trial_days: number;
  default_enabled: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  feature_pricing?: FeaturePricing[];
  override_count?: number;
}

export interface FeaturePricing {
  id: string;
  feature_id: string;
  currency: string;
  price_monthly: number;
  price_yearly: number;
  price_one_time: number | null;
  region: string;
  is_active: boolean;
}

export interface CompanyFeatureOverride {
  id: string;
  company_id: string;
  feature_id: string;
  enabled: boolean;
  enabled_at: string | null;
  disabled_at: string | null;
  expires_at: string | null;
  override_reason: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  feature?: { id: string; code: string; name: string; icon: string | null; category: string };
  company?: { id: string; name: string };
}

export interface ResolvedFeature {
  feature_code: string;
  feature_name: string;
  category: string;
  is_enabled: boolean;
  source: string;
  expires_at: string | null;
}

export interface UpdateFeatureRequest {
  name?: string;
  name_ar?: string;
  name_fr?: string;
  description?: string;
  is_premium?: boolean;
  default_enabled?: boolean;
  icon?: string;
  sort_order?: number;
}

export interface UpdatePricingRequest {
  currency: string;
  price_monthly: number;
  price_yearly: number;
  region: string;
}

export interface SetOverrideRequest {
  companyId: string;
  featureId: string;
  enabled: boolean;
  reason?: string;
  expiresAt?: string;
  createdBy: string;
}

export interface FeatureStats {
  totalFeatures: number;
  premiumFeatures: number;
  defaultEnabled: number;
  integrations: number;
}

// =====================================================
// SERVICE
// =====================================================

export const FeatureFlagService = {
  /**
   * Get all feature definitions with pricing
   */
  async getAllFeatures(): Promise<FeatureDefinition[]> {
    const { data, error } = await db
      .from('feature_definitions')
      .select('*, feature_pricing(*)')
      .order('sort_order', { ascending: true });

    if (error) throw new Error(`Failed to fetch features: ${error.message}`);

    // Get override counts per feature
    const { data: overrideCounts, error: countError } = await db
      .from('company_feature_overrides')
      .select('feature_id')
      .eq('enabled', true);

    const countsMap: Record<string, number> = {};
    if (!countError && overrideCounts) {
      for (const row of overrideCounts) {
        countsMap[row.feature_id] = (countsMap[row.feature_id] || 0) + 1;
      }
    }

    return (data || []).map((f: any) => ({
      ...f,
      override_count: countsMap[f.id] || 0,
    }));
  },

  /**
   * Update a feature definition
   */
  async updateFeature(id: string, updates: UpdateFeatureRequest): Promise<FeatureDefinition> {
    const { data, error } = await db
      .from('feature_definitions')
      .update(updates)
      .eq('id', id)
      .select('*, feature_pricing(*)')
      .single();

    if (error) throw new Error(`Failed to update feature: ${error.message}`);
    return data as FeatureDefinition;
  },

  /**
   * Update feature pricing (upsert)
   */
  async updateFeaturePricing(featureId: string, pricing: UpdatePricingRequest): Promise<void> {
    const { error } = await db
      .from('feature_pricing')
      .upsert(
        {
          feature_id: featureId,
          currency: pricing.currency,
          price_monthly: pricing.price_monthly,
          price_yearly: pricing.price_yearly,
          region: pricing.region,
          is_active: true,
        },
        { onConflict: 'feature_id,currency,region' }
      );

    if (error) throw new Error(`Failed to update pricing: ${error.message}`);
  },

  /**
   * Get all feature overrides for a specific company
   */
  async getCompanyOverrides(companyId: string): Promise<CompanyFeatureOverride[]> {
    const { data, error } = await db
      .from('company_feature_overrides')
      .select('*, feature:feature_definitions(id, code, name, icon, category)')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch company overrides: ${error.message}`);
    return (data || []) as unknown as CompanyFeatureOverride[];
  },

  /**
   * Get all company overrides for a specific feature
   */
  async getFeatureOverrides(featureId: string): Promise<CompanyFeatureOverride[]> {
    const { data, error } = await db
      .from('company_feature_overrides')
      .select('*, company:companies(id, name)')
      .eq('feature_id', featureId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch feature overrides: ${error.message}`);
    return (data || []) as unknown as CompanyFeatureOverride[];
  },

  /**
   * Set a company feature override (upsert)
   */
  async setCompanyFeatureOverride(params: SetOverrideRequest): Promise<void> {
    const { error } = await db
      .from('company_feature_overrides')
      .upsert(
        {
          company_id: params.companyId,
          feature_id: params.featureId,
          enabled: params.enabled,
          enabled_at: params.enabled ? new Date().toISOString() : null,
          disabled_at: !params.enabled ? new Date().toISOString() : null,
          expires_at: params.expiresAt || null,
          override_reason: params.reason || null,
          created_by: params.createdBy,
        },
        { onConflict: 'company_id,feature_id' }
      );

    if (error) throw new Error(`Failed to set override: ${error.message}`);
  },

  /**
   * Remove a company feature override (revert to plan/default)
   */
  async removeCompanyFeatureOverride(companyId: string, featureId: string): Promise<void> {
    const { error } = await db
      .from('company_feature_overrides')
      .delete()
      .eq('company_id', companyId)
      .eq('feature_id', featureId);

    if (error) throw new Error(`Failed to remove override: ${error.message}`);
  },

  /**
   * Get resolved features for a company (uses the SQL function)
   */
  async getCompanyFeatures(companyId: string): Promise<ResolvedFeature[]> {
    const { data, error } = await db.rpc('get_company_features', {
      p_company_id: companyId,
    });

    if (error) throw new Error(`Failed to get company features: ${error.message}`);
    return (data || []) as ResolvedFeature[];
  },

  /**
   * Get aggregate feature stats
   */
  async getFeatureStats(): Promise<FeatureStats> {
    const { data, error } = await db
      .from('feature_definitions')
      .select('is_premium, default_enabled, category');

    if (error) throw new Error(`Failed to fetch feature stats: ${error.message}`);

    const features = data || [];
    return {
      totalFeatures: features.length,
      premiumFeatures: features.filter((f: any) => f.is_premium).length,
      defaultEnabled: features.filter((f: any) => f.default_enabled).length,
      integrations: features.filter((f: any) => f.category === 'integration').length,
    };
  },
};
